import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import { useRef, useState, useCallback, useLayoutEffect, useEffect } from 'react';
import ModalContent from '../Modal/ModalContent.js';
import { Wrapper, Header, Handle, CloseButton, CloseButtonIcon } from './style.js';
import texts from './DraggableDrawer.texts.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import useGlobals from '../../hooks/useGlobals.js';
import useFocusTrap from '../../hooks/useFocusTrap.js';
import useDisableScroll from '../../hooks/useDisableScroll.js';
import { ICON_UI_CLOSE } from '../../icons/ICON_UI_CLOSE.js';

var DraggableDrawer = function DraggableDrawer(_ref) {
  var _ref$heading = _ref.heading,
    heading = _ref$heading === void 0 ? null : _ref$heading,
    _ref$children = _ref.children,
    children = _ref$children === void 0 ? null : _ref$children,
    _ref$className = _ref.className,
    className = _ref$className === void 0 ? '' : _ref$className,
    _ref$show = _ref.show,
    show = _ref$show === void 0 ? false : _ref$show,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    _ref$defaultHeight = _ref.defaultHeight,
    defaultHeight = _ref$defaultHeight === void 0 ? 60 : _ref$defaultHeight,
    onClose = _ref.onClose,
    _ref$nonClosable = _ref.nonClosable,
    nonClosable = _ref$nonClosable === void 0 ? false : _ref$nonClosable,
    _ref$noContentPadding = _ref.noContentPadding,
    noContentPadding = _ref$noContentPadding === void 0 ? false : _ref$noContentPadding;
  var wrapperRef = useRef(null);
  var handleRef = useRef(null);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isDragging = _useState2[0],
    setIsDragging = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isClosing = _useState4[0],
    setIsClosing = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    isOpening = _useState6[0],
    setIsOpening = _useState6[1];
  var _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    display = _useState8[0],
    setDisplay = _useState8[1];
  var _useState9 = useState(0),
    _useState10 = _slicedToArray(_useState9, 2),
    startY = _useState10[0],
    setStartY = _useState10[1];
  var _useState11 = useState(0),
    _useState12 = _slicedToArray(_useState11, 2),
    startHeight = _useState12[0],
    setStartHeight = _useState12[1];
  var _useState13 = useState(defaultHeight),
    _useState14 = _slicedToArray(_useState13, 2),
    drawerHeight = _useState14[0],
    setDrawerHeight = _useState14[1];
  var _useGlobals = useGlobals(),
    culture = _useGlobals.culture;
  var closeButtonLabel = texts[culture].close;
  var updateDrawerHeight = useCallback(function (height) {
    setDrawerHeight(height);
    wrapperRef.current.classList.toggle('fullscreen', height === 100);
  }, []);
  var hideDrawer = useCallback(function () {
    setIsOpening(false);
    setIsClosing(true);
    setTimeout(function () {
      wrapperRef.current.close();
      setIsClosing(false);
      setDisplay(false);
      onClose();
    }, 400);
  }, [onClose]);
  var handleDragStart = useCallback(function (e) {
    var _e$touches;
    setIsDragging(true);
    setStartY(e.pageY || ((_e$touches = e.touches) === null || _e$touches === void 0 ? void 0 : _e$touches[0]) && e.touches[0].pageY);
    setStartHeight(Number.parseInt(wrapperRef.current.style.height, 10));
  }, []);
  var handleDragging = useCallback(function (e) {
    var _e$touches$0$pageY, _e$touches2;
    if (!isDragging) return;
    var delta = startY - (e.pageY || ((_e$touches$0$pageY = (_e$touches2 = e.touches) === null || _e$touches2 === void 0 || (_e$touches2 = _e$touches2[0]) === null || _e$touches2 === void 0 ? void 0 : _e$touches2.pageY) !== null && _e$touches$0$pageY !== void 0 ? _e$touches$0$pageY : 0));
    var newHeight = startHeight + delta / window.innerHeight * 100;
    updateDrawerHeight(newHeight);
  }, [isDragging, startY, startHeight, updateDrawerHeight]);
  var handleDragStop = useCallback(function () {
    setIsDragging(false);
    var currentHeight = Number.parseInt(wrapperRef.current.style.height, 10);
    if (currentHeight < defaultHeight - 18) {
      hideDrawer();
    } else if (currentHeight > (100 - defaultHeight) * 0.3 + defaultHeight) {
      updateDrawerHeight(100);
    } else {
      updateDrawerHeight(defaultHeight);
    }
  }, [updateDrawerHeight, defaultHeight, hideDrawer]);
  var handleClickOutside = useCallback(function (e) {
    // Prevent onClose trigger if dragging up
    if (e.target !== wrapperRef.current) {
      return;
    }
    var dialogDimensions = wrapperRef.current.getBoundingClientRect();
    if (e.clientX === 0) {
      return;
    }
    if (e.clientX < dialogDimensions.left || e.clientX > dialogDimensions.right || e.clientY < dialogDimensions.top || e.clientY > dialogDimensions.bottom) {
      hideDrawer();
    }
  }, [hideDrawer]);

  /*
  Handle external show/hide
  (Layout effect ensures that the drawer’s height/style is applied
  before the browser paints the freshly-opened dialog)
   */
  // 
  // 
  useLayoutEffect(function () {
    if (!disabled) {
      var showDrawer = function showDrawer() {
        updateDrawerHeight(defaultHeight);
        setDisplay(true);

        // Delay to ensure dialog element display before calling modal + transition
        setTimeout(function () {
          wrapperRef.current.showModal();
          setIsOpening(true);
        }, 100);
      };
      if (show) {
        showDrawer();
      }
      if (!show && display) {
        // ← only hide if we know we’re actually showing
        hideDrawer();
      }
    }
    return undefined;
  }, [show, display, disabled, defaultHeight, hideDrawer, updateDrawerHeight]);
  useEffect(function () {
    var handleElement = handleRef === null || handleRef === void 0 ? void 0 : handleRef.current;
    var overlayElement = wrapperRef === null || wrapperRef === void 0 ? void 0 : wrapperRef.current;
    if (handleElement && overlayElement && !disabled && show) {
      handleElement.addEventListener('mousedown', handleDragStart);
      document.addEventListener('mousemove', handleDragging);
      document.addEventListener('mouseup', handleDragStop);
      handleElement.addEventListener('touchstart', handleDragStart);
      document.addEventListener('touchmove', handleDragging);
      document.addEventListener('touchend', handleDragStop);
      overlayElement.addEventListener('click', handleClickOutside);
      return function () {
        handleElement.removeEventListener('mousedown', handleDragStart);
        document.removeEventListener('mousemove', handleDragging);
        document.removeEventListener('mouseup', handleDragStop);
        handleElement.removeEventListener('touchstart', handleDragStart);
        document.removeEventListener('touchmove', handleDragging);
        document.removeEventListener('touchend', handleDragStop);
        overlayElement.removeEventListener('click', handleClickOutside);
      };
    }
    return undefined;
  }, [handleDragStart, handleDragging, handleDragStop, handleClickOutside, disabled, show]);
  useEffect(function () {
    if (!disabled && show) {
      var handleCloseEvent = function handleCloseEvent(e) {
        if (e.code === 'Escape') {
          hideDrawer();
        }
      };

      // Override native dialog cancel behaviour
      var handleCancelEvent = function handleCancelEvent(e) {
        e.preventDefault();
        if (e.type === 'cancel') {
          hideDrawer();
        }
      };
      var dialogElement = wrapperRef.current;
      dialogElement.addEventListener('cancel', handleCancelEvent);
      dialogElement.addEventListener('keydown', handleCloseEvent);
      return function () {
        dialogElement.removeEventListener('cancel', handleCancelEvent);
        dialogElement.removeEventListener('keydown', handleCloseEvent);
      };
    }
    return undefined;
  }, [hideDrawer, disabled, show]);
  useEffect(function () {
    if (typeof dialogPolyfill !== 'undefined' && typeof HTMLDialogElement !== 'function') {
      var dialog = wrapperRef.current;
      dialogPolyfill.registerDialog(dialog);
    }
  }, []);
  var enableHooks = !disabled && display;
  useFocusTrap(wrapperRef, enableHooks);
  useDisableScroll(enableHooks);

  // Respect iOS/Safari dynamic toolbar (if supported)
  var supportsDvh = typeof CSS !== 'undefined' && CSS.supports('height', '100dvh');
  var wrapperHeightStyle = {
    height: "".concat(drawerHeight).concat(supportsDvh ? 'dvh' : 'vh')
  };
  if (disabled) {
    return children;
  }
  return /*#__PURE__*/jsxs(Wrapper, {
    ref: wrapperRef,
    className: className,
    tabIndex: -1,
    "aria-hidden": !show && !isClosing
    // eslint-disable-next-line no-dupe-keys
    ,
    style: wrapperHeightStyle
    // @ts-ignore
    ,
    $display: display,
    $open: isOpening,
    $hide: isClosing,
    $noHeader: nonClosable,
    children: [!nonClosable && /*#__PURE__*/jsxs(Header, {
      children: [/*#__PURE__*/jsx(Handle, {
        ref: handleRef,
        children: /*#__PURE__*/jsx("span", {})
      }), /*#__PURE__*/jsx(CloseButton, {
        // @ts-ignore
        $show: show,
        type: "button",
        "aria-label": closeButtonLabel,
        onClick: function onClick() {
          return hideDrawer();
        },
        children: /*#__PURE__*/jsx(CloseButtonIcon, {
          path: ICON_UI_CLOSE,
          size: "xs",
          sizeMobile: "xs",
          "aria-hidden": "true"
        })
      })]
    }), /*#__PURE__*/jsx(ModalContent, {
      heading: heading,
      isInDrawer: true,
      noContentPadding: noContentPadding,
      children: children
    })]
  });
};
var DraggableDrawer$1 = DraggableDrawer;

export { DraggableDrawer$1 as default };
//# sourceMappingURL=DraggableDrawer.js.map
